import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { WalletConnect } from './WalletConnect';
import ThemeToggle from './ThemeToggle';
import { useTheme } from '../context/ThemeContext';
import { LogoIcon, DiscordIcon, TwitterIcon } from './SVGIcons';

const NavLinkItem = ({ to, label, active, onClick }: { to: string, label: string, active: boolean, onClick?: () => void }) => (
  <Link
    to={to}
    onClick={onClick}
    className={
      (active
        ? "font-semibold text-gray-900 dark:text-white"
        : "font-normal text-black/50 dark:text-white/50") +
      " flex items-center gap-[8px] transition-colors duration-200 text-sm font-borna"
    }
  >
    {active && (
      <span className="w-2 h-2 bg-[#00FFEE] rounded-full inline-block"></span>
    )}
    {label}
  </Link>
);

const Header: React.FC = () => {
  const location = useLocation();
  const { theme } = useTheme();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleNav = () => {
    setIsNavOpen(!isNavOpen);
  };

  const closeNav = () => {
    setIsNavOpen(false);
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={theme}
      />
      <nav className="relative z-[21]">
        <div className="max-w-[1312px] mx-auto px-[16px]">
          <div className="relative flex items-center justify-between h-[86px]">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 flex-shrink-0 z-10 hover:opacity-70 transition-opacity duration-200" onClick={closeMobileMenu}>
              <LogoIcon fillColor={theme === 'dark' ? 'white' : 'black'} className="h-8 w-8" />
              <span className="text-gray-900 dark:text-white font-bold text-xl">
                TaoFlow
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="flex items-center rounded-full px-[24px] py-[12px] bg-[#F2F2F2] dark:bg-[#262626] space-x-6 h-[46px]">
                <NavLinkItem to="/" label="Home" active={location.pathname === "/"} />
                <NavLinkItem to="/dashboard" label="Dashboard" active={location.pathname === "/dashboard"} />
                <NavLinkItem to="/subnets" label="Subnets" active={location.pathname.includes("/subnet")} />
                <NavLinkItem to="/trader" label="Trader" active={location.pathname.includes("/trader")} />
                <NavLinkItem to="/depositor" label="Depositor" active={location.pathname.includes("/depositor")} />
              </div>
            </div>

            {/* Desktop Right Side */}
            {location.pathname !== "/" && (
              <div className="hidden md:flex items-center z-100">
                <WalletConnect />
                <ThemeToggle />
              </div>
            )}

            {location.pathname === "/" && (
              <div className="gap-[12px] hidden md:flex">
                <Link to="/">
                  <button className="bg-[#28292E] rounded-full p-[10px]">
                    <TwitterIcon fillColor="white" />
                  </button>
                </Link>
                <Link to="https://discord.gg/Y6uC3v5ACZ">
                  <button className="bg-[#28292E] rounded-full p-[10px]">
                    <DiscordIcon fillColor="white" />
                  </button>
                </Link>
                {/* <Link to="/">
                  <button className="bg-[#28292E] rounded-full p-[10px]">
                    <TelegramIcon fillColor="white" />
                  </button>
                </Link> */}
              </div>
            )}

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center space-x-2">
              {location.pathname !== "/" && <ThemeToggle />}
              <button
                onClick={toggleNav}
                className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  {isNavOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Overlay */}
      <div
        className={`
          fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300
          ${isNavOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `}
        onClick={closeNav}
      />

      {/* Drawer */}
      <div
        className={`
          fixed top-0 right-0 h-full w-80 max-w-full bg-[#111] z-50
          transition-transform duration-300
          ${isNavOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <span className="text-xl font-bold text-white">TaoFlow</span>
          <button onClick={closeNav} className="text-white text-2xl">&times;</button>
        </div>
        {/* Nav Links */}
        <nav className="flex flex-col gap-2 px-6 py-4">
          <Link to="/" className="text-white text-left py-2" onClick={closeNav}>Dashboard</Link>
          <Link to="/trader" className="text-white text-left py-2" onClick={closeNav}>Trader</Link>
          <Link to="/depositor" className="text-white text-left py-2" onClick={closeNav}>Depositor</Link>
        </nav>
        <hr className="border-gray-700 my-2" />
        {/* Profile Section */}
        <div className="px-6 py-4">
          <WalletConnect mobile />
        </div>
      </div>
    </>
  );
};

export default Header; 